import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Live updates over WebSocket. Hardened after an audit pass:
 * - malformed frames no longer throw inside onmessage,
 * - reconnects back off exponentially instead of hammering a downed server,
 * - the socket is closed while the tab is hidden or offline and reopened when
 *   the tab becomes visible again,
 * - notification events reach the top-bar badge, and candidate events refresh
 *   an open profile page.
 */
export function useRealtime() {
  const queryClient = useQueryClient();
  useEffect(() => {
    let active = true;
    let socket: WebSocket | undefined;
    let timer: number | undefined;
    let attempt = 0;

    const connect = () => {
      if (!active || document.hidden || !window.navigator.onLine) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socket.onopen = () => { attempt = 0; };
      socket.onmessage = (event) => {
        let message: { type?: string };
        try { message = JSON.parse(event.data) as { type?: string }; }
        catch { return; } // A broken frame must not kill the connection handler.
        const type = message.type ?? "";
        // The funnel board keys off ["kanban", filters], so it needs its own
        // invalidation — a new application used to sit there until a reload.
        if (type.startsWith("candidate.")) {
          void queryClient.invalidateQueries({ queryKey: ["candidates"] });
          void queryClient.invalidateQueries({ queryKey: ["kanban"] });
          void queryClient.invalidateQueries({ queryKey: ["candidate"] });
        }
        // Applicants upload their CDL and medical card straight from the public
        // form, so the documents list has to learn about files no one in the CRM
        // uploaded.
        if (type.startsWith("document.")) {
          void queryClient.invalidateQueries({ queryKey: ["documents"] });
          void queryClient.invalidateQueries({ queryKey: ["candidate"] });
        }
        if (type.startsWith("vacancy.")) void queryClient.invalidateQueries({ queryKey: ["vacancies"] });
        if (type.startsWith("task.")) void queryClient.invalidateQueries({ queryKey: ["tasks"] });
        if (type.startsWith("interview.")) void queryClient.invalidateQueries({ queryKey: ["interviews"] });
        if (type.startsWith("notification.")) void queryClient.invalidateQueries({ queryKey: ["notifications"] });
        if (type) void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      };
      socket.onclose = () => {
        if (!active) return;
        // Cap the backoff at 30s; reset on successful open above.
        const delay = Math.min(30_000, 1_000 * 2 ** attempt++);
        timer = window.setTimeout(connect, delay);
      };
    };

    const scheduleIfIdle = () => {
      if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) connect();
    };
    const onVisible = () => { if (document.hidden) socket?.close(); else { attempt = 0; scheduleIfIdle(); } };
    const onOnline = () => { attempt = 0; scheduleIfIdle(); };
    const onOffline = () => socket?.close();

    connect();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (timer) window.clearTimeout(timer);
      socket?.close();
    };
  }, [queryClient]);
}
